import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import {Add, Refresh, Delete} from '@material-ui/icons';
import {BackButton, hasAtLeastOnePermission} from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization } from '../../sc-utilities';
import RoleDelete from '../../sc-roles/Roles/RoleDelete';
import Toolbar from '../../sc-utilities/Toolbar'
import * as action from "../../np-core/Auth/store/authActions";
import * as deviceActions from "./store/deviceCaseSupportedActions";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    folderStructureIcon: {
        marginRight: theme.spacing(),
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const DeviceCaseSupportedList = props => {
    const { classes, translations, permissions, tableColumns, redirectHandler, tableRef, activeCustomerID, saveColumnsFilter, onFetchDeviceCaseSupporteds, onBulkDeleteDeviceCaseSupporteds } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, ids: [] });

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['system_device_case_supported_delete']);

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            if (!hasPermission(permissions,'system_device_case_supported_delete')) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });
    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        const q = filterQuery(query.filters?? []);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        delete query["filters"];
        delete query["orderDirection"];
        delete query["search"];
        delete query["totalCount"];
        const result = await onFetchDeviceCaseSupporteds(query);
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data || [], page: query.page, pageSize: pageSize, totalCount: result.count || 0 };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['device_case_supported'] || 'Device case supported'}</Typography>
                {hasPermission(permissions, 'system_device_case_supported_create') &&
                <Fab
                    id="create_device"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/admin/device-case-supported/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab>}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>

            </div>
        </div>
    };
    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, deviceCaseSupportedID: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteDeviceCaseSupporteds({ DeviceCaseSupportedIDs: deleteModalState.deviceCaseSupportedID, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, deviceCaseSupportedID: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = deviceCaseSupportedID => {
        setDeleteModalState({ ...deleteModalState, open: true, deviceCaseSupportedID: deviceCaseSupportedID });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["device_case_supported"] || "Device case supported"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(device => device.DeviceCaseSupportedID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`DeviceCaseSupported`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }
    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'system_device_case_supported_delete') &&
            <RoleDelete
                entityName={translations['device_case_supported'] || 'Device case supported'}
                translations={translations}
                handleCloseDanger={handleCloseDanger}
                handleCloseSafe={handleCloseSafe}
                deleteModalState={deleteModalState}
            />}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onFetchDeviceCaseSupporteds: data => dispatch(deviceActions.fetchDeviceCaseSupporteds(data)),
        onBulkDeleteDeviceCaseSupporteds: data => dispatch(deviceActions.bulkDeleteDeviceCaseSupporteds(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(DeviceCaseSupportedList));

